/**
 * Desktop (`lg` and up) view of the PO / discount-code box: PO input, discount-code input with
 * Apply (cart or quote checkout, when the sales plant allows coupons) and the flexible-dates
 * checkbox (not for quotes). Stateless: `PurchaseOrderDiscountCode.tsx` holds the logic.
 * Unlike the mobile view it shows no terms-of-sale text (`OrderReviewDesktop.tsx` and
 * `ExpressCheckoutDesktop.tsx` render their own; the desktop cart page shows none) and marks
 * the PO required only when `purchaseOrderRequired` is set.
 */
import React from "react";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { Input, Checkbox, ErpInput } from "@ryerson/frontend.form-controls";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { validateErpRestrictedCharacters } from "@ryerson/frontend.common";
import { PurchaseOrderProps } from "./PurchaseOrderDiscountCode";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useApplication } from "@ryerson/frontend.application";
import { Alert } from "@ryerson/frontend.notification";
import { CheckOutType, useEcommerce } from "@ryerson/frontend.e-commerce";
import { MAX_FIELD_LENGTHS } from "@ryerson/frontend.common";
import { useFeatureToggles } from "@ryerson/frontend.feature-toggles";
import { maxDiscountCodeLength } from "@components/constants";

const GrayBox = styled(Flex)`
	width: 100%;
	padding: 20px;
	${(props: any) => {
		const { backgroundColor, showFlexDates } = props;
		return css`
			background-color: ${backgroundColor};
			border-radius: ${showFlexDates ? "2px 2px 0px 0px" : "2px"};
		`;
	}};
`;

const FlexibleDateSection = styled(Flex)`
	width: 100%;
	padding: 20px;
	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			border-radius: 0px 0px 2px 2px;
			background-color: ${backgroundColor};
		`;
	}};
`;

const captionText = css`
	margin-right: 10px;
`;

const POInput = styled(ErpInput)`
	margin-right: 20px;
	width: 100%;
	max-width: 180px;
`;

const OrderInput = styled(Input)`
	margin-right: 10px;
	width: 120px;
`;

/** Desktop PO / discount-code box. */
const PurchaseOrderDiscountCodeDesktop: React.FC<PurchaseOrderProps> = ({
	flexDates,
	testingId,
	showFlexDates,
	currentDiscountCode,
	setCurrentDiscountCode,
	updateDiscountCode,
	cartData,
	content,
	showDiscountMessage,
	discountMessage,
	setCurrentPurchaseOrderNumber,
	currentPurchaseOrderNumber,
	validationTriggered,
	purchaseOrderRequired,
	isSubmitted,
	POInputRef,
	isCouponAllowed = false,
}) => {
	const { theme } = useTheme();
	const { lighterGray, darkGray, secondaryBrand, error } = theme.colors.primary;

	const {
		localization: { language },
	} = useApplication();
	const { checkoutType } = useEcommerce();

	const { isFeatureEnabled } = useFeatureToggles();

	return (
		<>
			<GrayBox
				alignItems="center"
				justifyContent="space-between"
				{...{ backgroundColor: lighterGray, showFlexDates: showFlexDates }}
			>
				<Flex
					alignItems="center"
					css={{
						width: "100%",
						height:
							validationTriggered && currentPurchaseOrderNumber?.length === 0
								? 70
								: "100%",
					}}
					justifyContent="space-between"
				>
					<Flex alignItems="center" itemRef={POInputRef}>
						<Typography
							size="sm"
							variant="span"
							color={secondaryBrand}
							css={captionText}
						>
							{`${content.purchaseOrder}:`}
							{purchaseOrderRequired && (
								<Typography color={error}>&nbsp;*</Typography>
							)}
						</Typography>
						<POInput
							value={currentPurchaseOrderNumber}
							inputType="text"
							shape="rounded"
							onChange={(e) => {
								setCurrentPurchaseOrderNumber(
									e.target.value.substring(0, MAX_FIELD_LENGTHS.HEADER_PO)
								);
							}}
							css={{
								marginTop:
									validationTriggered && currentPurchaseOrderNumber?.length === 0
										? 40
										: 0,
							}}
							required={purchaseOrderRequired}
							language={language}
							disabled={cartData?.length === 0}
							dataTestId="purchase-order-input"
							submitted={isSubmitted}
							isValid={
								!validationTriggered ||
								(currentPurchaseOrderNumber?.length > 0 &&
									!validateErpRestrictedCharacters(currentPurchaseOrderNumber))
							}
							validation={validateErpRestrictedCharacters}
						/>
					</Flex>

					{isCouponAllowed &&
						(checkoutType === CheckOutType.CART ||
							checkoutType === CheckOutType.QUOTE) && (
							<Flex>
								<Flex alignItems="center">
									{showDiscountMessage ? (
										<FlexItem>
											<Alert
												severity={
													discountMessage === content.discountApplied
														? "success"
														: "error"
												}
												size="sm"
											>
												<Typography size="sm">{discountMessage}</Typography>
											</Alert>
										</FlexItem>
									) : (
										<Typography
											size="sm"
											variant="span"
											color={secondaryBrand}
											css={captionText}
										>
											{`${content.discountCode}:`}
										</Typography>
									)}
									<Flex>
										<OrderInput
											value={currentDiscountCode}
											inputType="text"
											shape="rounded"
											onChange={(e) => {
												setCurrentDiscountCode(e.target.value);
											}}
											language={language}
											disabled={cartData?.length === 0}
											dataTestId="discount-code-input"
											maxLength={maxDiscountCodeLength}
										/>
										<Button
											onClick={() => updateDiscountCode(false)}
											label={content.apply}
											shape="rounded"
											size="sm"
											type="secondary"
											background={darkGray}
											variant="outline"
											paddingType="relaxed"
											disabled={false}
											dataTestId="PO-discount-code-apply-button"
										/>
									</Flex>
								</Flex>
							</Flex>
						)}
				</Flex>
			</GrayBox>
			{checkoutType !== CheckOutType.QUOTE && showFlexDates && (
				<FlexibleDateSection
					alignItems="center"
					justifyContent="space-between"
					{...{ backgroundColor: darkGray }}
				>
					<Flex alignItems="center">
						<Checkbox
							id={testingId}
							value={content.flexibleDates}
							onChange={() => updateDiscountCode(true)}
							type="tertiary"
							checked={flexDates}
							size="sm"
							language={language}
							dataTestId="PO-discount-code-flexible-dates-checkbox"
						/>
					</Flex>
				</FlexibleDateSection>
			)}
		</>
	);
};

export default PurchaseOrderDiscountCodeDesktop;
